import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useForm } from 'react-hook-form';

import { Button, Table, TableCell, TableContainer, TableRow, TableHead, TableBody, TextField, Box, FormControl } from '@mui/material';

import useModals from 'components/modals/useModal';
import { modals } from 'components/modals/Modals';

import PageInfo from 'components/PageInfo';
import MasterSpecWriteLayer from 'components/layer/MasterSpecWriteLayer';
import MasterSpecExcelUploadLayer from 'components/layer/MasterSpecExcelUploadLayer';

import { addSpecKeys, deleteSpecKeys, getSpecKeysByQuery, updateSpecKeys } from 'components/axios/specAxios';

import queryString from 'query-string';
import CustomEnumSelect from 'components/select/CustomEnumSelect';
import { BootstrapInput } from 'data/data';
import useConfig from 'hooks/useConfig';

const SpecSetting = () => {
  const { mode } = useConfig();
  const { t } = useTranslation();
  const { openModal } = useModals();

  const handleAlertClick = (alertMessage) => {
    openModal(modals.alertModal, { message: alertMessage });
  };

  const { register: specRegister, handleSubmit: specHandleSubmit, setValue: specSetValue,
    formState: { isDirty: specIsDirty, errors: specErrors }
  } = useForm({});

  const {
    register: specUpdateRegister,
    handleSubmit: specUpdateHandleSubmit,
    reset: specUpdateReset,
    formState: { isDirty: specUpdateIsDirty, errors: specUpdateErrors }
  } = useForm({});

  const [editingId, setEditingId] = useState(null);

  const [searchField, setSearchField] = useState('name');
  const [searchKeyword, setSearchKeyword] = useState('');

  const [pageInfo, setPageInfo] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(process.env.REACT_APP_DEFAULT_PAGE_SIZE);

  const [specKeys, setSpecKeys] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isParam, setIsParam] = useState(false);

  const searchFields = [
    { code: "name", title: 'NAME' },
    { code: "code", title: 'KEY' }
  ];

  const qs = queryString.parse(window.location.search);

  const handlePaging = (value) => {
    value = isNaN(value) ? 1 : value;
    setCurrentPage(value - 1);
    setIsLoading(false);
  };

  useEffect(() => {

    if (qs) {
      if (qs.page) setCurrentPage(Number(qs.page));
      if (qs.size) setPageSize(qs.size);
      if (qs.searchField) setSearchField(qs.searchField);
      if (qs.searchKeyword) setSearchKeyword(qs.searchKeyword);
    }

    setIsParam(true);

  }, [qs.page, qs.size, qs.searchField, qs.searchKeyword]);

  useEffect(() => {

    if (!isLoading && !isNaN(currentPage) && isParam) {
      specKeyList();
    }

  }, [isLoading, isParam, currentPage]);

  const specKeyList = async (curPage = currentPage) => {
    const param = {
      page: curPage,
      size: pageSize,
      sort: "id,DESC"
    };

    if (searchKeyword) {
      const keyword = `contains(${searchKeyword})`;
      if (searchField === 'name') param.name = keyword;
      if (searchField === 'code') param.code = keyword;
    }

    const data = await getSpecKeysByQuery(param);
    if (data && data._embedded) {
      setPageInfo(data.page || {});
      setSpecKeys(data._embedded.specKeys || []);
    } else {
      setSpecKeys([]);
    }

    setIsLoading(true);
  };

  const handleInput = (event) => {
    event.preventDefault();
    setSearchKeyword(event.target.value.trim());
  };

  const handleSearch = (event) => {
    event.preventDefault();
    setCurrentPage(0);
    setPageInfo(prev => ({ ...prev, number: 0 }));

    specKeyList(0);
  };

  const handleOnKeyPress = e => {
    if (e.key === 'Enter') handleSearch(e);
  };

  // 수정폼 열기
  const handleEdit = (spec) => {
    setEditingId(spec.id);
    specUpdateReset({
      specIdx: spec.id,
      specNameUpdate: spec.name,
      specUnitUpdate: spec.unit
    });

  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  // 스펙 수정
  const onSubmitSpecKeyUpdate = (data) => {
    const body = {
      id: data.specIdx,
      name: data.specNameUpdate,
      unit: data.specUnitUpdate
    };

    openModal(modals.confirmModal, {
      onSubmit: () => {
        updateSpecKeys(body).then(() => {
          handleAlertClick(t('message.common.alert.modify.done'));
          setEditingId(null);
          setIsLoading(false);
        });
      },
      message: t('message.common.alert.modify.question')
    });
  };

  // 스펙 삭제
  const handleSpecKeyDeleteBtnClick = (id) => {
    openModal(modals.confirmModal, {
      onSubmit: () => {
        deleteSpecKeys(id).then(() => {
          handleAlertClick(t('message.common.alert.delete.done'));
          setIsLoading(false);
        });
      },
      message: t('message.common.alert.delete.question')
    });
  };

  // 스펙 등록
  const onSubmitSpecKey = (data) => {
    const body = {
      code: data.specCode,
      name: data.specName,
      unit: data.specUnit
    };

    openModal(modals.confirmModal, {
      onSubmit: () => {
        addSpecKeys(body).then(() => {
          handleAlertClick(t('message.common.alert.write.done'));
          specKeyList(0);

          specSetValue('specCode', '');
          specSetValue('specName', '');
          specSetValue('specUnit', '');
        });
      },
      message: t('message.common.alert.add.question')
    });

  };

  if (!isLoading) return null;

  return (
    <div className='cal'>
      <div className="search_section">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Box component="form">
            <div style={{ display: "inline-block" }}>
              <FormControl sx={{ m: 1, minWidth: 80 }}>
                <CustomEnumSelect
                  label={t('message.common.btn.search')}
                  name={'searchField'}
                  defaultValue={searchField}
                  onChange={(e) => setSearchField(e.target.value)}
                  list={searchFields}
                />
              </FormControl>

              <FormControl sx={{ m: 1 }} variant="standard">
                <BootstrapInput
                  style={{ width: "130px" }}
                  value={searchKeyword}
                  onChange={handleInput}
                  onKeyPress={handleOnKeyPress}
                />
              </FormControl>

              <Button
                variant="contained"
                style={{ marginTop: "12px", padding: "4px 15px" }}
                onClick={handleSearch}
              >
                {t('message.common.btn.search')}
              </Button>
            </div>
          </Box>

          <div className="btn_right">
            <div style={{ display: "flex" }}>
              <div style={{ padding: "5px" }}>
                <MasterSpecWriteLayer specKeyList={specKeyList} />
              </div>

              <div style={{ padding: "5px" }}>
                <MasterSpecExcelUploadLayer specKeyList={specKeyList} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Box 
        id="tableDiv"
        sx={{
          mt: 3,
          padding: "20px",
          borderRadius: "20px",
          overflow: "hidden",
          background: `${mode === 'dark' ? "#182131": "#fff"}`,
          border: `${mode === 'dark' ? "1px solid rgba(255,255,255,0.06)": "1px solid #30363d"}`,
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
        }}
      >
        <TableContainer>
          <Table className="list_table">
            <TableHead>
              <TableRow>
                <TableCell align="center">No.</TableCell>
                <TableCell align="center">KEY</TableCell>
                <TableCell align="center">NAME</TableCell>
                <TableCell align="center">{t('message.common.subtitle.unit')}</TableCell>
                <TableCell align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {specKeys.map((spec, i) => {
                const isEditing = editingId === spec.id;
                return (
                  <TableRow key={spec.id}>
                    <TableCell>{pageInfo.totalElements - (currentPage * pageInfo.size) - i}</TableCell>
                    <TableCell>{spec.code}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          {...specUpdateRegister("specNameUpdate", {
                            required: t('message.common.error.text.spec.name.input')
                          })}
                          sx={{ minWidth: '100%' }}
                        />
                      ) : spec.name}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          {...specUpdateRegister("specUnitUpdate")}
                          sx={{ minWidth: '100%' }}
                        />
                      ) : spec.unit}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <>
                          <input type="hidden" {...specUpdateRegister("specIdx")} />
                          <Button
                            variant="outlined"
                            color="success"
                            onClick={specUpdateHandleSubmit(onSubmitSpecKeyUpdate)}
                          >
                            {t('message.common.btn.modify')}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={handleCancelEdit}
                          >
                            {t('message.common.btn.cancel')}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outlined"
                            color="success"
                            onClick={() => handleEdit(spec)}
                          >
                            {t('message.common.btn.modify')}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleSpecKeyDeleteBtnClick(spec.id)}
                          >
                            {t('message.common.btn.delete')}
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <tfoot>
            <tr>
              <td colSpan={5}>&nbsp;</td>
            </tr>
            <tr>
              <td></td>
              <td>
                <TextField
                  aria-invalid={!specIsDirty ? undefined : specErrors.specCode ? "true" : "false"}
                  {...specRegister("specCode", {
                    required : t('message.common.error.text.spec.key.input')
                  })}
                  sx={{ minWidth: '100%' }}
                />
                {specErrors.specCode &&
                  <small role="alert">
                    <br />
                    {specErrors.specCode.message}
                  </small>
                }
              </td>

              <td>
                <TextField
                  aria-invalid={!specIsDirty ? undefined : specErrors.specName ? "true" : "false"}
                  {...specRegister("specName", {
                    required : t('message.common.error.text.spec.name.input')
                  })}
                  sx={{ minWidth: '100%' }}
                />
                {specErrors.specName &&
                  <small role="alert">
                    <br />
                    {specErrors.specName.message}
                  </small>
                }
              </td>

              <td>
                <TextField
                  {...specRegister("specUnit")}
                  sx={{ minWidth: '100%' }}
                />
              </td>

              <td style={{display:"flex", justifyContent:"space-evenly"}}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={specHandleSubmit(onSubmitSpecKey)}
                >
                {t('message.common.btn.add')}
                </Button>
              </td>

            </tr>
            </tfoot>
          </Table>
        </TableContainer>

        <PageInfo pageInfo={pageInfo} handlePaging={handlePaging} />
      </Box>
    </div>
  );
};

export default SpecSetting;